import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Plus, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", email: "", phone: "", company: "", customer_type: "private" };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/customers").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/customers", form);
      toast.success("Customer created");
      setCreateOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openDetail = async (c) => {
    try {
      const { data } = await api.get(`/customers/${c.id}`);
      setDetail(data);
      setNote("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/customers/${detail.id}/notes`, { note, channel: "note" });
      setNote("");
      openDetail(detail);
      toast.success("Note logged");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="customers-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Customers</h1>
          <p className="mt-1 text-sm text-muted-foreground">CRM core — contacts, history and lifetime value.</p>
        </div>
        <Button data-testid="new-customer-button" onClick={() => setCreateOpen(true)} className="rounded-md bg-ink hover:bg-ink-light">
          <Plus className="mr-2 h-4 w-4" /> New Customer
        </Button>
      </div>

      <div className="border border-border bg-white">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              {["Name", "Email", "Type", "Events", "Lifetime Value"].map((h) => (
                <th key={h} className="px-5 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => openDetail(c)} className="cursor-pointer border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3 font-medium">{c.name}{c.company && <span className="ml-2 text-xs text-muted-foreground">{c.company}</span>}</td>
                <td className="px-5 py-3 text-muted-foreground">{c.email}</td>
                <td className="px-5 py-3 capitalize">{c.customer_type}</td>
                <td className="px-5 py-3 tabular-nums">{c.event_count}</td>
                <td className="px-5 py-3 font-semibold tabular-nums">{fmt(c.lifetime_value)}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">No customers yet.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="new-customer-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Customer</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <div className="space-y-1.5"><Label>Name</Label><Input data-testid="customer-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="customer-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="customer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Company</Label><Input data-testid="customer-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                <SelectTrigger data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="corporate">Corporate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="customer-submit-button" type="submit" className="w-full rounded-md bg-ink hover:bg-ink-light">Create Customer</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-3xl bg-white" data-testid="customer-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{detail.name}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <div className="border border-border p-4 text-sm">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Profile</p>
                    <dl className="mt-3 space-y-2">
                      <div className="flex justify-between"><dt className="text-muted-foreground">Email</dt><dd className="font-medium">{detail.email}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Phone</dt><dd className="font-medium">{detail.phone || "—"}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Type</dt><dd className="font-medium capitalize">{detail.customer_type}</dd></div>
                      <div className="flex justify-between"><dt className="text-muted-foreground">Lifetime value</dt><dd className="font-bold text-brand tabular-nums" data-testid="customer-ltv">{fmt(detail.lifetime_value)}</dd></div>
                    </dl>
                  </div>
                  <div className="border border-border p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Bookings</p>
                    <div className="mt-3 space-y-2 text-sm">
                      {(detail.bookings || []).map((b) => (
                        <div key={b.id} className="flex flex-wrap items-center justify-between gap-3" data-testid={`customer-booking-${b.id}`}>
                          <span className="font-mono text-xs font-semibold">{b.booking_ref}</span>
                          <span className="text-muted-foreground tabular-nums">{b.event_date}</span>
                          <StatusBadge status={b.status} />
                        </div>
                      ))}
                      {(detail.bookings || []).length === 0 && <p className="text-muted-foreground">No bookings yet.</p>}
                    </div>
                  </div>
                </div>
                <div className="border border-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Communication history</p>
                  <div className="mt-4 max-h-64 space-y-0 overflow-y-auto">
                    {[...(detail.communications || [])].reverse().map((c, i) => (
                      <div key={i} className="relative border-l-2 border-border pb-4 pl-4" data-testid={`comm-entry-${i}`}>
                        <div className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-brand" />
                        <p className="text-xs text-muted-foreground tabular-nums">{format(new Date(c.at), "dd MMM yyyy, HH:mm")} · {c.channel}</p>
                        <p className="mt-0.5 text-sm">{c.note}</p>
                      </div>
                    ))}
                    {(detail.communications || []).length === 0 && <p className="text-sm text-muted-foreground">No history yet.</p>}
                  </div>
                  <div className="mt-4 flex gap-2 border-t border-border pt-4">
                    <Input data-testid="add-note-input" placeholder="Log a call, email or note…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} />
                    <Button data-testid="add-note-button" onClick={addNote} className="rounded-md bg-ink hover:bg-ink-light"><Send className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
